import { Component } from 'react'
import { env } from '../lib/env'
import { telemetry } from '../lib/telemetry'

/**
 * Boundary genérico. Aceita `scope` (identificador da área) e `fallback`
 * (componente customizado). Captura só erros de render. Erros async/handlers
 * devem ser tratados no hook/caller.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    telemetry.captureException(error, {
      scope: this.props.scope ?? 'global',
      componentStack: info.componentStack,
    })
  }

  reset = () => this.setState({ error: null })

  render() {
    if (!this.state.error) return this.props.children
    const Fallback = this.props.fallback ?? DefaultFallback
    return <Fallback error={this.state.error} reset={this.reset} />
  }
}

function DefaultFallback({ error, reset }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 text-center">
      <h2 className="text-lg font-semibold text-foreground">Algo deu errado</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        A tela encontrou um erro inesperado.
        {env.IS_DEV && (
          <>
            <br />
            <code className="mt-2 inline-block text-xs">{error.message}</code>
          </>
        )}
      </p>
      <button
        type="button"
        onClick={reset}
        className="mt-6 rounded-2xl bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
      >
        Tentar novamente
      </button>
    </div>
  )
}

function RouteFallback({ error, reset }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <h2 className="text-base font-semibold text-foreground">Erro nesta seção</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Algo falhou ao renderizar esta área. O resto do portal continua funcionando.
        {env.IS_DEV && (
          <>
            <br />
            <code className="mt-2 inline-block text-xs">{error.message}</code>
          </>
        )}
      </p>
      <button
        type="button"
        onClick={reset}
        className="mt-4 rounded-xl bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
      >
        Recarregar seção
      </button>
    </div>
  )
}

export function RouteErrorBoundary({ children, scope }) {
  return (
    <ErrorBoundary scope={scope ?? 'route'} fallback={RouteFallback}>
      {children}
    </ErrorBoundary>
  )
}
